import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { User, Session } from "@supabase/supabase-js";

export const PLUS_CONFIG = {
  monthly: {
    priceId: "price_1T9VFJHfNR53zPiHv7FX6NXV",
    productId: "prod_U7kkodSbaXvbTb",
  },
  yearly: {
    priceId: "price_1T9VFnHfNR53zPiHAaFNCil6",
    productId: "prod_U7klpy3bIOUeq1",
  },
} as const;

const PLUS_PRODUCT_IDS = new Set([
  PLUS_CONFIG.monthly.productId,
  PLUS_CONFIG.yearly.productId,
  "prod_U7MkdLQsvOpYPb", // legacy/trial product
  "admin_unlimited",     // admin override
  "referral_reward",     // referral-based Plus
  "signup_welcome",      // 1 week free for new signups
]);

interface SubscriptionState {
  isPlus: boolean;
  isTrial: boolean;
  subscriptionEnd: string | null;
  loading: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscription: SubscriptionState;
  signOut: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  subscription: { isPlus: false, isTrial: false, subscriptionEnd: null, loading: false },
  signOut: async () => {},
  refreshSubscription: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionState>({
    isPlus: false,
    isTrial: false,
    subscriptionEnd: null,
    loading: false,
  });
  const initializedRef = useRef(false);

  const checkSubscription = useCallback(async () => {
    if (!session) {
      setSubscription({ isPlus: false, isTrial: false, subscriptionEnd: null, loading: false });
      return;
    }
    setSubscription((s) => ({ ...s, loading: true }));
    try {
      // Validate the session is still valid first
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      if (userError || !currentUser) {
        console.warn("[Auth] Session invalid during subscription check, signing out");
        await supabase.auth.signOut();
        setSubscription({ isPlus: false, isTrial: false, subscriptionEnd: null, loading: false });
        return;
      }

      const { data: { session: freshSession } } = await supabase.auth.getSession();
      const tokenToUse = freshSession?.access_token ?? session.access_token;
      
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${tokenToUse}` },
      });
      if (error) throw error;
      setSubscription({
        isPlus: data?.subscribed && PLUS_PRODUCT_IDS.has(data?.product_id),
        isTrial: data?.is_trial ?? false,
        subscriptionEnd: data?.subscription_end ?? null,
        loading: false,
      });
    } catch {
      setSubscription((s) => ({ ...s, loading: false }));
    }
  }, [session]);

  useEffect(() => {
    // Get initial session first
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.warn("[Auth] Session retrieval error, clearing state:", error.message);
        setSession(null);
        setUser(null);
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }
      setLoading(false);
      initializedRef.current = true;
    });

    // Then listen for future changes only
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!initializedRef.current) return; // skip the initial echo
        if (event === 'TOKEN_REFRESHED') {
          console.log("[Auth] Token refreshed successfully");
        }
        if (event === 'SIGNED_OUT') {
          console.log("[Auth] User signed out");
        }
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => authSub.unsubscribe();
  }, []);

  // Check subscription when session changes
  useEffect(() => {
    if (session) {
      checkSubscription();
      // Fire-and-forget: update last_seen_at
      void supabase.rpc("touch_last_seen" as never).then(() => {}, () => {});
    }
  }, [session, checkSubscription]);

  // Periodic refresh every 5 minutes
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(checkSubscription, 300000);
    return () => clearInterval(interval);
  }, [session, checkSubscription]);

  // Claim referral code after signup/email verification
  const referralClaimedRef = useRef(false);
  useEffect(() => {
    if (!user || !session || referralClaimedRef.current) return;
    const storedRef = localStorage.getItem("gatornotes_ref_code");
    if (!storedRef) return;

    referralClaimedRef.current = true;
    const claimReferral = async () => {
      try {
        const res = await supabase.functions.invoke("claim-referral", {
          body: { referral_code: storedRef },
        });
        if (res.data?.success) {
          toast({ title: "🎉 You earned 1 week of Plus!", description: "Your referral reward has been applied." });
          checkSubscription(); // refresh subscription status
        } else if (res.data?.error) {
          console.log("Referral claim:", res.data.error);
        }
      } catch (err) {
        console.error("Referral claim error:", err);
      } finally {
        localStorage.removeItem("gatornotes_ref_code");
      }
    };
    claimReferral();
  }, [user, session, checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, subscription, signOut, refreshSubscription: checkSubscription }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
